import { isNullOrUndefined } from 'util';
import { DetailFilter } from '@components/detailFilter';
import LiveFilter from '@components/liveFilter';
import { cn } from '@utils/helpers';
import { ChevronDownIcon } from 'lucide-react';
import type * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFragment } from 'react-relay';
import { useNavigate } from 'react-router-dom';
import { FundsListDownload } from '../fundsListDownload';
import { fundsTableFragment } from './FundsTable.relay';
import { Fund } from './lib/fund';
import { FundsTableHeader } from './lib/header';
import type { FundsTablePropsType, SortObjectType } from './types';
import { SortOrder } from './types';

const EmptyMessage: React.FC<{ children: React.ReactNode }> = ({ children }) => <div className="p-2.5 text-center">{children}</div>;

const sortConfig = {
  fundsname: 0,
  category: 0,
  isin: 1,
  distribution: 0,
  currency: 0,
};

const detailFilterBooleanVal = ['institutional'];
const parsedArrays = {};

const FundsTable: React.FC<FundsTablePropsType> = (props) => {
  const navigate = useNavigate();

  const table = useFragment(fundsTableFragment, props.table);

  const tableHeaderColsRef = useRef(null);
  const tableResolvedFundsRef = useRef<any[]>([]);
  const rowDefaultHeightRef = useRef(0);
  const frameIdRef = useRef<number | null>(null);

  const getInitialState = () => {
    let liveFilterInfo = {};
    let detailFilterInfo = {};
    let detailFilterVisible = false;

    if (props.location.state && props.location.state.fundsList) {
      liveFilterInfo = props.location.state.fundsList.liveFilterInfo || {};
      detailFilterInfo = props.location.state.fundsList.detailFilterInfo || {};
      detailFilterVisible = props.location.state.fundsList.detailFilterVisible || false;
    }

    return { liveFilterInfo, detailFilterInfo, detailFilterVisible };
  };

  const initialState = getInitialState();
  const [sort, setSort] = useState<[SortObjectType, SortObjectType]>([
    { col: 'fundsname', order: SortOrder.DESC },
    { col: 'isin', order: SortOrder.DESC },
  ]);
  const [liveFilterInfo, setLiveFilterInfo] = useState<object>(initialState.liveFilterInfo);
  const [detailFilterInfo, setDetailFilterInfo] = useState<object>(initialState.detailFilterInfo);
  const [detailFilterVisible, setDetailFilterVisible] = useState<boolean>(initialState.detailFilterVisible);

  useEffect(() => {
    if (props.location.state && props.location.state.fundsList) {
      setLiveFilterInfo(props.location.state.fundsList.liveFilterInfo || {});
      setDetailFilterInfo(props.location.state.fundsList.detailFilterInfo || {});
      setDetailFilterVisible(props.location.state.fundsList.detailFilterVisible || false);
    }
  }, [props.location.state]);

  const shrinkSingleNameElement = useCallback((el: HTMLElement) => {
    const width = el.offsetWidth;
    const height = el.offsetHeight;

    let w = width;
    for (; w > 0; w -= 1) {
      el.style.width = w + 'px';
      if (el.offsetHeight !== height) {
        break;
      }
    }

    el.style.width = w + 1 + 'px';
  }, []);

  const shrinkNames = useCallback(() => {
    const collection = document.querySelectorAll('.Tranche .Tranche-name');
    for (const parent of collection) {
      const el = parent.querySelector('.FundName-inline');
      if (!el) {
        continue;
      }

      const htmlEl = el as HTMLElement;
      htmlEl.style.width = '';
      if (htmlEl.offsetHeight > rowDefaultHeightRef.current) {
        shrinkSingleNameElement(htmlEl);
      }
    }
  }, [shrinkSingleNameElement]);

  const shrinkEvent = useCallback(() => {
    if (frameIdRef.current) {
      cancelAnimationFrame(frameIdRef.current);
    }
    frameIdRef.current = requestAnimationFrame(shrinkNames);
  }, [shrinkNames]);

  useEffect(() => {
    const testContainer = document.createElement('div');
    testContainer.style.display = 'block';
    testContainer.style.visibility = 'hidden';
    testContainer.style.position = 'absolute';

    const testElementWrapper = document.createElement('div');
    testElementWrapper.setAttribute('class', 'Tranche-name');

    const testElement = document.createElement('div');
    testElement.setAttribute('class', 'FundName-inline');
    testElement.innerText = '0';

    testElementWrapper.appendChild(testElement);
    testContainer.appendChild(testElementWrapper);
    (document.body || document.documentElement).appendChild(testContainer);
    rowDefaultHeightRef.current = testElement.offsetHeight;

    testContainer.outerHTML = '';

    shrinkNames();

    window.addEventListener('resize', shrinkEvent);

    return () => {
      if (frameIdRef.current) {
        cancelAnimationFrame(frameIdRef.current);
      }
      window.removeEventListener('resize', shrinkEvent);
    };
  }, [shrinkNames, shrinkEvent]);

  useEffect(() => {
    shrinkNames();
  });

  const getFundsListOrEmpty = useCallback(() => {
    return (props.location.state && props.location.state.fundsList) || {};
  }, [props.location.state]);

  const liveFilterHandler = useCallback((cols: Array<string>, p): void => {
    const filterInfo = cols.reduce((acc, col) => {
      acc[col] = [p.target.value];
      return acc;
    }, {});
    setLiveFilterInfo(filterInfo);
  }, []);

  const onLiveFilterBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      navigate(
        { pathname: props.location.pathname },
        {
          state: {
            ...props.location.state,
            fundsList: {
              ...getFundsListOrEmpty(),
              liveFilterInfo: liveFilterInfo,
            },
          },
        },
      );
    },
    [navigate, props.location, getFundsListOrEmpty, liveFilterInfo],
  );

  const preparseFilterVals = useCallback((filterVals: Array<string>) => {
    filterVals.forEach(getParsedFilterArrayValue);
  }, []);

  const getParsedFilterArrayValue = useCallback((filterValue: string) => {
    if (parsedArrays[filterValue]) {
      return parsedArrays[filterValue];
    }

    if (filterValue.substr(0, 1) === '[' && filterValue.substr(-1) === ']') {
      try {
        parsedArrays[filterValue] = JSON.parse(filterValue);
        return parsedArrays[filterValue];
      } catch (e) {
        return filterValue;
      }
    }
  }, []);

  const detailFilterHandlerBoolean = useCallback(
    (col: string, filterVals: Array<string>): Array<string> => {
      let vals = [...filterVals].splice(0, 1);

      if (detailFilterInfo[col] && detailFilterInfo[col][0] === vals[0]) {
        vals = [];
      }

      return vals;
    },
    [detailFilterInfo],
  );

  const detailFilterHandler = useCallback(
    (col: string, filterVals: Array<string>): void => {
      if (isNullOrUndefined(col)) {
        setDetailFilterInfo({});
        navigate(
          { pathname: props.location.pathname },
          {
            state: {
              ...props.location.state,
              fundsList: {
                ...getFundsListOrEmpty(),
                detailFilterInfo: {},
              },
            },
          },
        );
      } else {
        if (detailFilterBooleanVal.indexOf(col) > -1) {
          filterVals = detailFilterHandlerBoolean(col, filterVals);
        }

        const filterInfo = { ...detailFilterInfo };

        if (filterVals.length === 0) {
          delete filterInfo[col];
        } else {
          filterInfo[col] = filterVals;
          preparseFilterVals(filterVals);
        }

        setDetailFilterInfo(filterInfo);
        navigate(
          { pathname: props.location.pathname },
          {
            state: {
              ...props.location.state,
              fundsList: {
                ...getFundsListOrEmpty(),
                detailFilterInfo: filterInfo,
              },
            },
          },
        );
      }
    },
    [navigate, props.location, getFundsListOrEmpty, detailFilterInfo, detailFilterHandlerBoolean, preparseFilterVals],
  );

  const toggleDetailFilter = useCallback(
    (e: any): void => {
      setDetailFilterVisible(!detailFilterVisible);
      navigate(
        { pathname: props.location.pathname },
        {
          state: {
            ...props.location.state,
            fundsList: {
              ...getFundsListOrEmpty(),
              detailFilterVisible: !detailFilterVisible,
            },
          },
        },
      );
    },
    [navigate, props.location, getFundsListOrEmpty, detailFilterVisible],
  );

  const nullBottomSortFn = useCallback(
    (sortCol: string, sortOrder: SortOrder, a, b): number => {
      if (!(sortCol in a.node)) {
        return 0;
      }
      if (!(sortCol in b.node)) {
        return 0;
      }

      if (a.node[sortCol] === null) {
        return 1;
      }
      if (b.node[sortCol] === null) {
        return -1;
      }

      return a.node[sortCol].localeCompare(b.node[sortCol], props.currentLanguage, { caseFirst: 'false' }) * sortOrder || a.node.fundsname.localeCompare(b.node.fundsname, props.currentLanguage, { caseFirst: 'false' });
    },
    [props.currentLanguage],
  );

  const handleSort = useCallback((sortCol: string): void => {
    setSort(
      (prevSort) =>
        prevSort.map((level, idx) => {
          return sortConfig[sortCol] === idx ? { col: sortCol, order: -level.order } : level;
        }) as [SortObjectType, SortObjectType],
    );
  }, []);

  const filterTrancheSalesAdmission = useCallback(
    (tranche: any): boolean => {
      if (!tranche.node.salesadmission.length) {
        return false;
      }

      return tranche.node.salesadmission.indexOf(props.salesAdmission) > -1;
    },
    [props.salesAdmission],
  );

  const compareByFieldType = useCallback(
    (fieldName: string, value: any, obj: any) => {
      const fieldTypes = {
        institutional: 'boolean',
        distribution: 'array',
      };

      let useValue: string | Array<string> = value;

      switch (fieldTypes[fieldName]) {
        case 'boolean':
          useValue = value === 'true' || value === '1' ? '1' : value === 'false' || value === '0' ? '0' : null;
          break;

        case 'array':
          useValue = getParsedFilterArrayValue(value);
          break;

        default:
          useValue = value.toLowerCase();
          break;
      }

      if (typeof useValue === 'object' && useValue.constructor === Array) {
        return obj[fieldName] && useValue.indexOf(obj[fieldName]) >= 0;
      }

      let checkValue = obj[fieldName] || '';
      checkValue = checkValue.toLowerCase ? checkValue.toLowerCase() : checkValue;
      return checkValue.indexOf(useValue) >= 0;
    },
    [getParsedFilterArrayValue],
  );

  const filterColumn = useCallback(
    (filterInfo: any, fund: any, tranche: any, logicalOr: boolean): boolean => {
      const keys = Object.keys(filterInfo);
      if (!keys.length) {
        return true;
      }

      return keys.reduce<boolean>((filterStatus, columnName) => {
        const filterValues = filterInfo[columnName].filter((item) => item !== '');
        if (!filterValues.length) {
          return logicalOr;
        }

        const newFilterStatus = filterValues.reduce((innerFilterStatus, columnValue) => {
          if (innerFilterStatus) {
            return innerFilterStatus;
          }

          if (columnValue === '') {
            return false;
          }

          if (compareByFieldType(columnName, columnValue, tranche)) {
            return true;
          }
          if (compareByFieldType(columnName, columnValue, fund)) {
            return true;
          }

          return false;
        }, false);

        return logicalOr ? newFilterStatus || filterStatus : newFilterStatus && filterStatus;
      }, !logicalOr);
    },
    [compareByFieldType],
  );

  const resolveCallback = useCallback((callback) => {
    tableResolvedFundsRef.current.push(callback);
  }, []);

  const filterTranches = useCallback(
    (tranches, fund) => {
      return tranches.filter((tranche) => {
        if (!filterTrancheSalesAdmission(tranche)) {
          return false;
        }

        if (!filterColumn(liveFilterInfo, fund, tranche.node, true)) {
          return false;
        }

        if (!filterColumn(detailFilterInfo, fund, tranche.node, false)) {
          return false;
        }

        return true;
      });
    },
    [filterTrancheSalesAdmission, filterColumn, liveFilterInfo, detailFilterInfo],
  );

  const filterTableEntries = useCallback(
    (entries) => {
      let count = 0;
      const filtered = entries.edges
        .map((entry) => {
          if (entry.node.largeinvestmentfonds === 2) {
            return false;
          }

          const clonedEntry = JSON.parse(JSON.stringify(entry));
          const fund = clonedEntry.node;
          const tranches = filterTranches(clonedEntry.node.tranches.edges, fund);

          if (tranches.length > 0) {
            count += tranches.length;
            clonedEntry.node.tranches.edges = tranches;
            return clonedEntry;
          }

          return false;
        })
        .filter((entry) => entry !== false);

      return {
        result: filtered,
        count,
      };
    },
    [filterTranches],
  );

  const filtered = filterTableEntries(table.entries);
  tableResolvedFundsRef.current = [];
  const tableResults = filtered.result;

  const sortCol = sort[0].col;
  const sortOrder = sort[0].order;

  const subsortCol = sort[1].col;
  const subsortOrder = sort[1].order;

  const sortInfo = {
    sortCol: sort[0].col,
    sortOrder: sort[0].order,
  };

  const filteredCounter = filtered.count;
  const results = tableResults.sort(nullBottomSortFn.bind(null, sortCol, sortOrder));

  return (
    <div className="w-full">
      <div className="w-full border-b border-[#d0d0d0] pb-5">
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <LiveFilter
            liveFilter={table.livefilter}
            onBlur={onLiveFilterBlur}
            filterHandlerFn={liveFilterHandler}
            filterText={Object.values(liveFilterInfo)[0] || ''}
            className="w-full rounded-[20px] border border-[#d0d0d0] bg-[url(/static/front/assets/other-icons/search.png)] bg-position-[right_8px_center] bg-no-repeat px-[15px] py-[5px] pr-[35px] text-sm focus-within:border-[#a0a0a0] focus-within:outline-none tablet:max-w-lg"
            filterCols={['fundsname', 'isin']}
          />
          <button type="button" onClick={toggleDetailFilter} className="cursor-pointer text-left text-sm text-accent no-underline flex items-center gap-1">
            {detailFilterVisible ? table.filterbuttontextexpanded : table.filterbuttontext}
            <ChevronDownIcon className={cn('w-4 h-4 transition-transform duration-300', detailFilterVisible && 'rotate-180')} />
          </button>
        </div>
        <DetailFilter
          isVisible={detailFilterVisible}
          detailFilter={table.detailfilter}
          filterHandlerFn={detailFilterHandler}
          filterVals={detailFilterInfo}
          filteredCounter={filteredCounter}
          radio={false}
          hasResetOption={Object.keys(detailFilterInfo).length > 0}
        />
      </div>

      <FundsListDownload
        fundsListDownload={props.downloadOverview}
        salesAdmission={props.salesAdmission}
        className="mt-[15px] inline-flex items-center text-xs text-[#636d6d] no-underline after:ml-0.5 after:inline-block after:h-5 after:w-5 after:bg-[url(/static/front/assets/document-icons/xlsx.png)] after:bg-no-repeat after:bg-size-[20px]"
      />
      <table className="Table FundsSelection-table mt-20 mb-10 [&&_thead_th_img]:translate-y-px max-[620px]:block max-[620px]:[&&_thead]:block max-[620px]:[&&_tbody]:block max-[620px]:[&&_thead_tr]:flex max-[620px]:[&&_thead_tr_th]:block max-[620px]:[&&_thead_tr_th]:w-full max-[620px]:[&&_thead_tr_th:not(:first-child)]:hidden">
        <colgroup>
          <col span={1} />
          <col className="total" />
        </colgroup>

        <FundsTableHeader
          header={table}
          sortHandler={handleSort}
          sortInfo={sortInfo}
          getHeaderCallback={(header) => {
            tableHeaderColsRef.current = header;
          }}
        />

        {results.map((fundRow) => {
          return (
            <Fund
              key={fundRow.node.id}
              sortFn={nullBottomSortFn.bind(null, subsortCol, subsortOrder)}
              fund={fundRow.node}
              liveFilterInfo={liveFilterInfo}
              detailFilterInfo={detailFilterInfo}
              detailFilterVisible={detailFilterVisible}
              salesadmissionFilter={filterTrancheSalesAdmission}
              sortConfig={sortConfig}
              filterFn={filterTranches}
              salesAdmission={props.salesAdmission}
              resolveFund={resolveCallback}
              location={props.location}
            />
          );
        })}
        {results.length === 0 ? (
          <tbody>
            <tr>
              <td colSpan={5}>
                <EmptyMessage>{table.noResultsMessage}</EmptyMessage>
              </td>
            </tr>
          </tbody>
        ) : null}
      </table>
    </div>
  );
};

export { FundsTable };
export { SortOrder };
